import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopImagePromptEditorPage.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import DesktopImageGalleryPage from '../DesktopImageGallery/DesktopImageGalleryPage';
import { Skeleton } from '../../effects';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import { API_CONFIG } from '../../../config/api';
import AIRegenerationModal from '../../Mobile/MobilePostReview/AIRegenerationModal';
import postService from '../../../services/PostService';
import useHoldToRevealPreviousImage from '../common/hooks/useHoldToRevealPreviousImage';

const BackIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AIIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819Z" fill="currentColor"/>
  </svg>
);

const AILoadingOverlay = ({ text = '' }) => (
  <div className="loopino-image-ai-loading">
    <div className="loopino-image-ai-loading-dim" />
    <div className="loopino-image-ai-loading-content">
      <div className="loopino-image-ai-icon">
        <AIIcon size={22} />
        <div className="loopino-image-ai-pulse" />
      </div>
      <p className="loopino-image-ai-text">
        {text}
        <span className="loopino-image-ai-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </p>
    </div>
  </div>
);

const HTTP_PROTOCOL_REGEX = /^https?:\/\//i;
const BLOB_URL_REGEX = /^blob:/i;
const DATA_URL_REGEX = /^data:/i;
const PROMPT_MIN_LENGTH = 10;
const PROMPT_MAX_LENGTH = 1000;
const PROMPT_BLOCK_REGEX = /^\s*([^:]{1,80}):( ?)([\s\S]*)$/;

const PROMPT_KEY_ALIASES = {
  soggetto: 'subject',
  subject: 'subject',
  composizione: 'composition',
  composition: 'composition',
  stile: 'style',
  style: 'style',
  camera: 'camera',
  luce: 'lighting',
  lighting: 'lighting',
  illuminazione: 'lighting',
  tono: 'tone',
  tone: 'tone',
  qualita: 'quality',
  qualità: 'quality',
  quality: 'quality',
  colori: 'color_palette',
  palette: 'color_palette',
  'palette_colori': 'color_palette',
  mood: 'mood',
  atmosfera: 'mood',
  notes: 'additional_notes',
  note: 'additional_notes',
  'note_aggiuntive': 'additional_notes',
  'altri_dettagli': 'additional_notes',
  'additional notes': 'additional_notes',
  'additional_notes': 'additional_notes',
  'detagli': 'additional_notes',
  metadata: 'metadata',
  instructions: 'instructions'
};

const PROMPT_JSON_KEY_ORDER = [
  'subject',
  'composition',
  'style',
  'camera',
  'lighting',
  'mood',
  'tone',
  'quality',
  'color_palette',
  'additional_notes'
];

const RESERVED_PROMPT_JSON_KEYS = new Set(['metadata', 'reference_images', 'instructions']);

const PROMPT_LABEL_DEFAULTS = {
  en: {
    subject: 'Subject',
    composition: 'Composition',
    style: 'Style',
    camera: 'Camera',
    lighting: 'Lighting',
    mood: 'Mood',
    tone: 'Tone',
    quality: 'Quality',
    color_palette: 'Color palette',
    additional_notes: 'Additional notes'
  },
  it: {
    subject: 'Soggetto',
    composition: 'Composizione',
    style: 'Stile',
    camera: 'Camera',
    lighting: 'Illuminazione',
    mood: 'Atmosfera',
    tone: 'Tono',
    quality: 'Qualità',
    color_palette: 'Palette colori',
    additional_notes: 'Note aggiuntive'
  }
};

const BLOCK_PLACEHOLDER_EXAMPLES = {
  subject: 'Es: Un cane con le gambe di coccodrillo',
  composition: 'Es: Inquadratura dall’alto su un picnic un po’ pasticciato',
  style: 'Es: Stile fumetto anni 90, colori super saturi',
  camera: 'Es: Scatto fish-eye da skater che fa una piega',
  lighting: 'Es: Luci al neon da discoteca anni 80',
  mood: 'Es: Atmosfera festa sorpresa con amici che ridono',
  tone: 'Es: Tono ironico ma curato, zero formalità',
  quality: 'Es: Alta definizione ma niente posa da catalogo',
  color_palette: 'Es: Palette gelato pistacchio, fragola e limone',
  additional_notes: 'Es: Niente gatti volanti stavolta (forse)',
  default: 'Es: Aggiungi qui cosa vuoi vedere generato'
};

const normalizeLabelToKey = (label, fallbackIndex = 0) => {
  if (!label) {
    return `section_${fallbackIndex + 1}`;
  }

  const normalized = label
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_');

  if (!normalized) {
    return `section_${fallbackIndex + 1}`;
  }

  return PROMPT_KEY_ALIASES[normalized] || normalized;
};

const parsePromptBlock = (block) => {
  const trimmed = typeof block === 'string' ? block.trim() : '';
  if (!trimmed) {
    return { label: '', value: '' };
  }

  const match = /^\s*([^:]{1,80}):\s*(.*)$/.exec(trimmed);
  if (match) {
    return {
      label: match[1].trim(),
      value: match[2].trim()
    };
  }

  return {
    label: '',
    value: trimmed
  };
};

const clampCreativity = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return undefined;
  }
  return Math.min(1, Math.max(0, parseFloat(numeric.toFixed(2))));
};

const formatBytes = (bytes, decimals = 1) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))}${sizes[i]}`;
};

const useAutosizeTextarea = (textareaRef, value) => {
  useLayoutEffect(() => {
    const el = textareaRef?.current;
    if (!el) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [textareaRef, value]);
};

const AutoGrowTextarea = ({ value, className = '', onChange, ...props }) => {
  const textareaRef = useRef(null);
  useAutosizeTextarea(textareaRef, value);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      className={className}
      onChange={onChange}
      {...props}
    />
  );
};

const DualOptionToggle = ({
  active = false,
  onChange,
  leftLabel,
  rightLabel,
  disabled = false,
  ariaLabel = '',
  className = ''
}) => (
  <button
    type="button"
    className={`dual-option-toggle ${active ? 'checked' : 'unchecked'} ${disabled ? 'disabled' : ''} ${className}`}
    onClick={() => {
      if (disabled) return;
      onChange?.(!active);
    }}
    aria-pressed={active}
    aria-label={ariaLabel || (active ? rightLabel : leftLabel)}
    disabled={disabled}
  >
    <span className="dual-option-toggle-track" aria-hidden="true" />
    <span className="dual-option-toggle-label dual-option-toggle-off">{leftLabel}</span>
    <span className="dual-option-toggle-label dual-option-toggle-on">{rightLabel}</span>
  </button>
);

const useEstimatedProgressPercent = (isActive, durationMs = 90000, capPercent = 99) => {
  const [percent, setPercent] = useState(0);
  const startAtRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      startAtRef.current = null;
      setPercent(0);
      return undefined;
    }

    startAtRef.current = Date.now();
    setPercent(0);

    const update = () => {
      const startAt = startAtRef.current;
      if (!startAt) {
        setPercent(0);
        return;
      }
      const elapsed = Math.max(0, Date.now() - startAt);
      const ratio = durationMs > 0 ? Math.min(1, elapsed / durationMs) : 1;
      const next = Math.min(capPercent, Math.floor(ratio * capPercent));
      setPercent((prev) => (prev === next ? prev : next));
    };

    update();
    const intervalId = setInterval(update, 250);
    return () => clearInterval(intervalId);
  }, [capPercent, durationMs, isActive]);

  return percent;
};

const EstimatedProgressBar = ({ label, percent, className = '', ariaLabel = '' }) => {
  const safePercent = Math.min(99, Math.max(0, Number.isFinite(percent) ? percent : 0));
  return (
    <div
      className={`loopino-estimated-progress ${className}`.trim()}
      role="progressbar"
      aria-label={ariaLabel || label}
      aria-valuemin={0}
      aria-valuemax={99}
      aria-valuenow={safePercent}
    >
      <div className="loopino-estimated-progress-fill" style={{ width: `${safePercent}%` }} />
      <div className="loopino-estimated-progress-meta">
        <span className="loopino-estimated-progress-label">{label}</span>
        <span className="loopino-estimated-progress-percent">{safePercent}%</span>
      </div>
    </div>
  );
};

const toAbsoluteMediaUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const base = API_CONFIG?.BASE_URL || (typeof window !== 'undefined' ? window.location?.origin : undefined);
    const resolved = new URL(trimmed, base);
    return resolved.href;
  } catch (error) {
    return trimmed;
  }
};

const withCacheBuster = (url, token) => {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (!token) {
    return url;
  }

  try {
    const base = API_CONFIG?.BASE_URL || (typeof window !== 'undefined' ? window.location?.origin : undefined);
    const resolved = new URL(url, base);
    resolved.searchParams.set('_v', String(token));
    return resolved.href;
  } catch (error) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_v=${encodeURIComponent(String(token))}`;
  }
};

const extractRegeneratedImageUrls = (response = {}, options = {}) => {
  const urls = new Set();

  const pushUrl = (candidate) => {
    if (!candidate || typeof candidate !== 'string') {
      return;
    }
    const trimmed = candidate.trim();
    if (!trimmed) {
      return;
    }
    const resolved = toAbsoluteMediaUrl(trimmed) || trimmed;
    if (resolved) {
      urls.add(resolved);
    }
  };

  const pushFromObject = (candidate) => {
    if (!candidate) {
      return;
    }
    if (typeof candidate === 'string') {
      pushUrl(candidate);
      return;
    }
    if (typeof candidate !== 'object') {
      return;
    }
    pushUrl(candidate.url);
    pushUrl(candidate.image_url);
    pushUrl(candidate.public_url);
    pushUrl(candidate.source_url);
    pushUrl(candidate.file_url);
    pushUrl(candidate.media_url);
    pushUrl(candidate.local_url);
  };

  const collectFromContainer = (container) => {
    if (!container || typeof container !== 'object') {
      return;
    }

    if (Array.isArray(container.regenerated_images)) {
      container.regenerated_images.forEach(pushUrl);
    }

    if (Array.isArray(container?.regenerated_content?.media_urls)) {
      container.regenerated_content.media_urls.forEach(pushUrl);
    }

    if (container?.image_url) {
      pushUrl(container.image_url);
    }

    if (Array.isArray(container?.images)) {
      container.images.forEach(pushFromObject);
    }

    if (Array.isArray(container?.media_urls)) {
      container.media_urls.forEach(pushUrl);
    }

    if (Array.isArray(container?.media)) {
      container.media.forEach(pushFromObject);
    }

    const metadataImages = container?.regeneration_metadata?.generated_images;
    if (Array.isArray(metadataImages)) {
      metadataImages.forEach(pushFromObject);
    }
  };

  collectFromContainer(response);
  collectFromContainer(response?.post);
  collectFromContainer(response?.data?.post);
  collectFromContainer(response?.result?.post);
  collectFromContainer(response?.results?.post);

  if (Array.isArray(response?.images)) {
    response.images.forEach(pushFromObject);
  }

  if (Array.isArray(response?.results?.images)) {
    response.results.images.forEach(pushFromObject);
  }

  const allUrls = Array.from(urls);
  const shouldExclude = Boolean(options?.excludeExisting);
  const existingCandidates = Array.isArray(options?.existingUrls) ? options.existingUrls : [];

  if (!shouldExclude || existingCandidates.length === 0) {
    return allUrls;
  }

  const existingUrls = new Set(
    existingCandidates
      .filter((candidate) => typeof candidate === 'string')
      .map((candidate) => candidate.trim())
      .filter(Boolean)
      .map((candidate) => toAbsoluteMediaUrl(candidate) || candidate)
  );

  return allUrls.filter((url) => !existingUrls.has(url));
};

const buildPromptJsonFromBlocks = (blocks = [], options = {}) => {
  const {
    normalizedRatio,
    quality,
    tone,
    creativity,
    simplePrompt,
    referenceImages = [],
    mode,
    baseReferenceIncluded = false,
    includeReferenceStats = false
  } = options;

  const promptJson = {};

  blocks.forEach((block, index) => {
    const { label, value } = parsePromptBlock(block);
    if (!value) {
      return;
    }
    const key = normalizeLabelToKey(label || `section_${index + 1}`, index);
    if (!key || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    promptJson[key] = value;
  });

  const metadata = {};
  if (normalizedRatio) {
    metadata.aspect_ratio = normalizedRatio;
  }
  if (quality) {
    metadata.quality = quality;
  }
  if (tone) {
    metadata.tone = tone;
  }
  const normalizedCreativity = clampCreativity(creativity);
  if (normalizedCreativity !== undefined) {
    metadata.creativity = normalizedCreativity;
  }
  if (mode) {
    metadata.mode = mode;
  }
  if (includeReferenceStats) {
    if (typeof baseReferenceIncluded === 'boolean') {
      metadata.base_reference_included = baseReferenceIncluded;
    }
    if (Array.isArray(referenceImages)) {
      metadata.reference_images_supplied = referenceImages.length;
    }
  }

  if (Object.keys(metadata).length > 0) {
    promptJson.metadata = metadata;
  }

  if (Array.isArray(referenceImages) && referenceImages.length > 0) {
    promptJson.reference_images = referenceImages;
  }

  if (simplePrompt) {
    promptJson.instructions = simplePrompt;
  }

  return promptJson;
};

const buildPromptJsonSchema = (promptJson = {}) => {
  const schema = {};
  Object.keys(promptJson).forEach((key) => {
    if (RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    schema[key] = null;
  });
  return Object.keys(schema).length > 0 ? schema : null;
};

const buildBlocksFromPromptJson = (promptJson, resolveLabel) => {
  if (!promptJson || typeof promptJson !== 'object') {
    return [];
  }

  const blocks = [];
  const visited = new Set();

  const appendBlock = (key) => {
    if (!key || visited.has(key) || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }

    const value = promptJson[key];
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return;
    }

    let content = value;
    if (typeof value === 'object') {
      try {
        content = JSON.stringify(value);
      } catch (error) {
        content = String(value);
      }
    }

    const label = resolveLabel ? resolveLabel(key) : key;
    blocks.push(`${label}: ${content}`);
    visited.add(key);
  };

  PROMPT_JSON_KEY_ORDER.forEach(appendBlock);

  Object.keys(promptJson).forEach((key) => {
    appendBlock(key);
  });

  return blocks;
};

const resolveReferenceCandidate = (rawValue) => {
  const value = typeof rawValue === 'string' ? rawValue.trim() : '';

  if (!value) {
    return { url: null, reason: 'empty', value };
  }

  if (BLOB_URL_REGEX.test(value)) {
    return { url: null, reason: 'blob', value };
  }

  if (DATA_URL_REGEX.test(value)) {
    return { url: null, reason: 'data', value };
  }

  if (HTTP_PROTOCOL_REGEX.test(value)) {
    return { url: value, reason: null, value };
  }

  try {
    const resolved = new URL(value, API_CONFIG?.BASE_URL || undefined);
    if (resolved && HTTP_PROTOCOL_REGEX.test(resolved.href)) {
      return { url: resolved.href, reason: null, value };
    }
  } catch (error) {
    if (typeof window !== 'undefined' && window.location) {
      try {
        const fallbackResolved = new URL(value, window.location.origin);
        if (HTTP_PROTOCOL_REGEX.test(fallbackResolved.href)) {
          return { url: fallbackResolved.href, reason: null, value };
        }
      } catch (fallbackError) {
        console.warn('[DesktopImagePromptEditor] Failed to resolve reference URL with window origin', {
          value,
          error: fallbackError
        });
      }
    }

    console.warn('[DesktopImagePromptEditor] Failed to resolve reference URL with API base', {
      value,
      error
    });
  }

  return { url: null, reason: 'invalid', value };
};

const gcd = (a, b) => {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const temp = y;
    y = x % y;
    x = temp;
  }
  return x || 1;
};

const splitPromptIntoBlocks = (prompt) => {
  if (!prompt) return [];
  const byLines = prompt.split('\n').map((p) => p.trim()).filter(Boolean);
  if (byLines.length > 1) return byLines;
  const bySemi = prompt.split(';').map((p) => p.trim()).filter(Boolean);
  if (bySemi.length > 1) return bySemi;
  const bySentence = prompt.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter(Boolean);
  if (bySentence.length > 1) return bySentence;
  return [prompt.trim()];
};

const DesktopImagePromptEditorPageInner = ({
  post,
  imageIndex = 0,
  onBack,
  onRegenerate,
  isLoading = false,
  onNavigate,
  routeState = null,
  queryString = ''
}) => {
  const { t, language } = useLanguage();
  const { notify, confirm } = useDesktopAlert();
  const apiClient = useApi();

  const searchParams = useMemo(() => {
    try {
      if (queryString) {
        return new URLSearchParams(queryString);
      }
      if (typeof window !== 'undefined') {
        return new URLSearchParams(window.location.search || '');
      }
    } catch (error) {
      return null;
    }
    return null;
  }, [queryString]);

  const queryImageIndex = useMemo(() => {
    if (!searchParams) return null;
    const raw = searchParams.get('imageIndex');
    if (raw === null) return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }, [searchParams]);

  const queryPostId = useMemo(() => {
    if (!searchParams) return null;
    return searchParams.get('postId') || null;
  }, [searchParams]);

  const resolvedImageIndex = useMemo(() => {
    if (routeState && typeof routeState.promptEditorImageIndex === 'number') {
      return routeState.promptEditorImageIndex;
    }
    if (queryImageIndex !== null) {
      return queryImageIndex;
    }
    if (typeof imageIndex === 'number') return imageIndex;
    return 0;
  }, [imageIndex, queryImageIndex, routeState]);

  const resolvedPost = useMemo(() => {
    if (post) return post;
    if (routeState && routeState.promptEditorPost) {
      return routeState.promptEditorPost;
    }
    return null;
  }, [post, routeState]);

  const fallbackImageName = useMemo(
    () => t('postReview.imageEditor.fallback.image', { index: resolvedImageIndex + 1 }),
    [t, resolvedImageIndex]
  );
  const fallbackReferenceName = useMemo(
    () => t('postReview.imageEditor.fallback.reference'),
    [t]
  );

  const languageCode = useMemo(() => (
    typeof language === 'string' && language
      ? language.split('-')[0]?.toLowerCase() || 'en'
      : 'en'
  ), [language]);

  const labelDefaults = useMemo(() => (
    PROMPT_LABEL_DEFAULTS[languageCode] || PROMPT_LABEL_DEFAULTS.en
  ), [languageCode]);

  const buildEmptyBlocksFromMock = useCallback((idx) => {
    const title = resolvedPost?.title || t('postReview.imageEditor.defaults.titleFallback', { index: idx + 1 });
    const brand = resolvedPost?.company_name || resolvedPost?.brand || t('postReview.imageEditor.defaults.brandFallback');
    const lines = t('postReview.imageEditor.defaults.prompt', {
      title,
      brand,
      returnObjects: true
    });

    if (!Array.isArray(lines) || !lines.length) {
      return [''];
    }

    return lines.map((line, lineIdx) => {
      const { label } = parsePromptBlock(line);
      const safeLabel = label || t('postReview.imageEditor.blocks.defaultLabel', { defaultValue: `Blocco ${lineIdx + 1}` });
      return `${safeLabel}: `;
    });
  }, [resolvedPost, t]);

  const buildBlockPlaceholder = useCallback((label, idx) => {
    const normalizedKey = normalizeLabelToKey(label, idx);
    const example = BLOCK_PLACEHOLDER_EXAMPLES[normalizedKey] || BLOCK_PLACEHOLDER_EXAMPLES.default;
    return example;
  }, []);

  const initialPrompt = useMemo(() => {
    const candidates = [
      resolvedPost?.imagesPrompts?.[resolvedImageIndex],
      resolvedPost?.revised_prompt,
      resolvedPost?.original_prompt,
      resolvedPost?.ai_prompt,
      ''
    ];
    return (candidates.find(Boolean) || '').trim();
  }, [resolvedPost, resolvedImageIndex]);

  const [blocks, setBlocks] = useState(() => {
    const b = splitPromptIntoBlocks(initialPrompt);
    return b.length ? b : buildEmptyBlocksFromMock(resolvedImageIndex);
  });
  const [creativity, setCreativity] = useState(0.5);
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [customWidth, setCustomWidth] = useState('1024');
  const [customHeight, setCustomHeight] = useState('1024');
  const [quality, setQuality] = useState('standard');
  const [tone, setTone] = useState('modern');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(() => (resolvedPost ? 'simple' : 'pro'));
  const [simplePromptText, setSimplePromptText] = useState('');
  const [promptImage, setPromptImage] = useState(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const promptImageObjectUrlRef = useRef(null);
  const promptJsonSignatureRef = useRef(null);
  const [showPromptBlocksModal, setShowPromptBlocksModal] = useState(false);
  const [isRegeneratingBlocks, setIsRegeneratingBlocks] = useState(false);
  const lastGeneratedImageRef = useRef(null);
  const generationPendingRef = useRef(false);
  const [hasGeneratedImage, setHasGeneratedImage] = useState(false);
  const [regeneratedImageUrl, setRegeneratedImageUrl] = useState(null);
  const [regeneratedImageToken, setRegeneratedImageToken] = useState(null);
  const [isApplyingImageToPost, setIsApplyingImageToPost] = useState(false);
  const [referenceError, setReferenceError] = useState('');
  const [isDraggingReference, setIsDraggingReference] = useState(false);
  const referenceInputRef = useRef(null);
  const pageHeaderRef = useRef(null);
  const imageEditorGridRef = useRef(null);
  const imagePreviewPanelRef = useRef(null);
  const imagePreviewPlaceholderRef = useRef(null);
  const [previewStickyTop, setPreviewStickyTop] = useState(() => 36);
  const previewPanelStyle = useMemo(() => {
    if (!Number.isFinite(previewStickyTop)) return undefined;
    return { '--loopino-desktop-preview-sticky-top': `${previewStickyTop}px` };
  }, [previewStickyTop]);
  const [previewPlaceholderHeight, setPreviewPlaceholderHeight] = useState(0);
  const previewPlaceholderStyle = useMemo(
    () => ({ height: `${Math.max(0, Number(previewPlaceholderHeight) || 0)}px` }),
    [previewPlaceholderHeight]
  );
  const [previewDynamicStyle, setPreviewDynamicStyle] = useState({});
  const [previewPinMode, setPreviewPinMode] = useState('static'); // static | fixed | bottom
  const previewRafRef = useRef(null);
  const referenceMaxSizeBytes = 5 * 1024 * 1024;
  const referenceMaxSizeMB = Math.round(referenceMaxSizeBytes / (1024 * 1024));
  const allowedReferenceTypes = useMemo(() => ([
    'image/png',
    'image/jpeg',
    'image/jpg'
  ]), []);

  const currentMediaItem = useMemo(() => {
    if (!resolvedPost || !Array.isArray(resolvedPost.mediaArray)) {
      return null;
    }
    const item = resolvedPost.mediaArray[resolvedImageIndex];
    if (item) {
      return item;
    }
    return resolvedPost.mediaArray[0] || null;
  }, [resolvedPost, resolvedImageIndex]);

  const currentImage = useMemo(() => {
    if (!resolvedPost || !Array.isArray(resolvedPost.images)) return null;
    const image = resolvedPost.images[resolvedImageIndex] ?? resolvedPost.images[0] ?? null;
    return image || null;
  }, [resolvedPost, resolvedImageIndex]);

  const currentImageUrl = useMemo(() => {
    const fromImages = (() => {
      if (!currentImage) return null;
      if (typeof currentImage === 'string') return currentImage;
      return currentImage.url || currentImage.imageUrl || currentImage.src || null;
    })();

    if (fromImages) {
      return fromImages;
    }

    if (!currentMediaItem) {
      return null;
    }

    return (
      currentMediaItem.url ||
      currentMediaItem.previewUrl ||
      currentMediaItem.imageUrl ||
      currentMediaItem.src ||
      null
    );
  }, [currentImage, currentMediaItem]);

  const currentImageName = useMemo(() => {
    if (typeof currentImage === 'object' && currentImage) {
      return currentImage.name || currentImage.title || currentImage.alt || fallbackImageName;
    }
    return fallbackImageName;
  }, [currentImage, fallbackImageName]);

  const postId = useMemo(() => {
    if (resolvedPost) {
      const originalDataId = resolvedPost.originalData?.id || resolvedPost.originalData?._id;
      return resolvedPost.id || resolvedPost._id || resolvedPost.postId || originalDataId || null;
    }
    if (queryPostId) {
      return queryPostId;
    }
    return null;
  }, [queryPostId, resolvedPost]);

  const editingSessionKey = useMemo(
    () => `${postId || 'unknown'}-${resolvedImageIndex}`,
    [postId, resolvedImageIndex]
  );

  const isModifyMode = mode === 'simple';

  const effectiveCurrentImageUrl = useMemo(() => (
    regeneratedImageUrl || currentImageUrl || null
  ), [currentImageUrl, regeneratedImageUrl]);

  const resolveLabelForKey = useCallback((key) => {
    if (!key) {
      return '';
    }

    const normalized = key.toString().toLowerCase();
    const fallbackDefault = labelDefaults[normalized] || normalized
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    const translationKey = normalized.replace(/[^a-z0-9]+/g, '_');

    return t(`postReview.imageEditor.promptLabels.${translationKey}`, {
      defaultValue: fallbackDefault || key
    });
  }, [labelDefaults, t]);

  const applyPreviewLayout = useCallback((nextMode, nextStyle, placeholderHeight) => {
    setPreviewPinMode((prevMode) => (prevMode === nextMode ? prevMode : nextMode));
    setPreviewDynamicStyle((prevStyle) => {
      const keys = ['position', 'top', 'left', 'width', 'height'];
      const isSame = keys.every((key) => prevStyle?.[key] === nextStyle?.[key]);
      return isSame ? prevStyle : nextStyle;
    });
    const nextHeight = Number.isFinite(placeholderHeight) ? placeholderHeight : 0;
    setPreviewPlaceholderHeight((prevHeight) => (prevHeight === nextHeight ? prevHeight : nextHeight));
  }, []);

  const recomputePreviewPosition = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const previewNode = imagePreviewPanelRef.current;
    const gridNode = imageEditorGridRef.current;
    const placeholderNode = imagePreviewPlaceholderRef.current;

    if (!previewNode || !gridNode || !placeholderNode || isLoading || showImageGallery) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const stickyTop = Number.isFinite(previewStickyTop) ? previewStickyTop : 32;

    if (window.innerWidth <= 1100) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const previewHeight = previewNode.offsetHeight || 0;
    if (!previewHeight) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const placeholderRect = placeholderNode.getBoundingClientRect();
    const previewWidth = placeholderRect.width || previewNode.offsetWidth || 0;

    const containerRect = gridNode.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const containerTop = containerRect.top + scrollY;
    const containerBottom = containerRect.bottom + scrollY;

    const startStick = containerTop - stickyTop;
    const endStick = containerBottom - stickyTop - previewHeight;

    if (scrollY >= endStick) {
      applyPreviewLayout(
        'bottom',
        {
          position: 'absolute',
          top: `${Math.max(0, containerRect.height - previewHeight)}px`,
          left: '0px',
          width: `${previewWidth}px`,
          height: 'auto'
        },
        previewHeight
      );
      return;
    }

    if (scrollY >= startStick) {
      applyPreviewLayout(
        'fixed',
        {
          position: 'fixed',
          top: `${stickyTop}px`,
          left: `${placeholderRect.left + scrollX}px`,
          width: `${previewWidth}px`,
          height: 'auto'
        },
        previewHeight
      );
      return;
    }

    applyPreviewLayout(
      'static',
      {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        width: '100%',
        height: 'auto'
      },
      0
    );
  }, [applyPreviewLayout, isLoading, previewStickyTop, showImageGallery]);

  const schedulePreviewUpdate = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (previewRafRef.current) {
      return;
    }
    previewRafRef.current = window.requestAnimationFrame(() => {
      previewRafRef.current = null;
      recomputePreviewPosition();
    });
  }, [recomputePreviewPosition]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updatePreviewStickyTop = () => {
      try {
        const offsets = [32];
        const headerNode = pageHeaderRef.current;
        if (headerNode instanceof HTMLElement) {
          const headerRect = headerNode.getBoundingClientRect();
          if (Number.isFinite(headerRect.bottom)) {
            offsets.push(headerRect.bottom + 12);
          }
        }

        const shellNode = document.querySelector('.loopino-desktop-image-editor-shell');
        if (shellNode instanceof HTMLElement) {
          const styles = window.getComputedStyle(shellNode);
          const paddingTop = parseFloat(styles.paddingTop || '0');
          if (!Number.isNaN(paddingTop)) {
            offsets.push(paddingTop - 12);
          }
        }

        const offset = Math.max(20, Math.min(160, Math.max(...offsets)));
        if (Number.isFinite(offset)) {
          setPreviewStickyTop((prev) => (prev === offset ? prev : offset));
        }
      } catch (error) {
        setPreviewStickyTop((prev) => (Number.isFinite(prev) ? prev : 36));
      }
    };

    updatePreviewStickyTop();
    window.addEventListener('resize', updatePreviewStickyTop);
    return () => {
      window.removeEventListener('resize', updatePreviewStickyTop);
    };
  }, [isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    if (showImageGallery || isLoading) {
      if (previewRafRef.current) {
        window.cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
      setPreviewPinMode('static');
      setPreviewDynamicStyle({});
      setPreviewPlaceholderHeight(0);
      return undefined;
    }

    schedulePreviewUpdate();

    const handleScroll = () => schedulePreviewUpdate();
    const handleResize = () => schedulePreviewUpdate();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (previewRafRef.current) {
        window.cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
    };
  }, [isLoading, schedulePreviewUpdate, showImageGallery]);

  useEffect(() => {
    if (!editingSessionKey) {
      return;
    }

    const resolvedBlocks = (() => {
      const primary = splitPromptIntoBlocks(initialPrompt);
      if (primary.length) {
        return primary;
      }
      const fallback = buildEmptyBlocksFromMock(resolvedImageIndex);
      return fallback.length ? fallback : [''];
    })();
    setBlocks(resolvedBlocks);

    setSimplePromptText('');

    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[DesktopImagePromptEditor] Failed to revoke object URL during session reset', error);
      }
      promptImageObjectUrlRef.current = null;
    }
    setPromptImage(null);

    promptJsonSignatureRef.current = null;
    lastGeneratedImageRef.current = null;
    generationPendingRef.current = false;
    setHasGeneratedImage(false);
    setRegeneratedImageUrl(null);
    setRegeneratedImageToken(null);

    const nextMode = resolvedPost ? 'simple' : 'pro';
    setMode(nextMode);
    setCreativity(0.5);
    setQuality('standard');
    setTone('modern');
    setAspectRatio('1:1');
    setCustomWidth('1024');
    setCustomHeight('1024');
    setReferenceError('');
    setIsDraggingReference(false);
  }, [buildEmptyBlocksFromMock, currentImageUrl, editingSessionKey, initialPrompt, resolvedImageIndex, resolvedPost]);

  const existingPromptJson = useMemo(() => {
    const parseCandidate = (candidate) => {
      if (!candidate) {
        return null;
      }

      if (typeof candidate === 'string') {
        try {
          return JSON.parse(candidate);
        } catch (error) {
          console.warn('[DesktopImagePromptEditor] Unable to parse prompt_json string', error);
          return null;
        }
      }

      if (typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate;
      }

      return null;
    };

    const mediaFile = Array.isArray(resolvedPost?.media_files) ? resolvedPost.media_files[resolvedImageIndex] : null;
    const candidates = [
      currentMediaItem?.prompt_json,
      currentMediaItem?.metadata?.prompt_json,
      currentMediaItem?.ai_metadata?.prompt_json,
      currentMediaItem?.generation_metadata?.prompt_json,
      mediaFile?.prompt_json,
      mediaFile?.metadata?.prompt_json,
      resolvedPost?.prompt_json,
      resolvedPost?.promptJson,
      Array.isArray(resolvedPost?.images_prompt_json) ? resolvedPost.images_prompt_json[resolvedImageIndex] : null,
      Array.isArray(resolvedPost?.imagesPromptsJson) ? resolvedPost.imagesPromptsJson[resolvedImageIndex] : null,
      resolvedPost?.regeneration_metadata?.prompt_json,
      Array.isArray(resolvedPost?.regeneration_metadata?.generated_images)
        ? resolvedPost.regeneration_metadata.generated_images[resolvedImageIndex]?.prompt_json
        : null,
      resolvedPost?.__pendingRegeneratedResponse?.regeneration_metadata?.prompt_json,
      Array.isArray(resolvedPost?.__pendingRegeneratedResponse?.regeneration_metadata?.generated_images)
        ? resolvedPost.__pendingRegeneratedResponse.regeneration_metadata.generated_images[resolvedImageIndex]?.prompt_json
        : null,
      resolvedPost?.__pendingRegeneratedResponse?.regenerated_content?.prompt_json
    ];

    for (const candidate of candidates) {
      const parsed = parseCandidate(candidate);
      if (parsed) {
        return parsed;
      }
    }

    return null;
  }, [currentMediaItem, resolvedImageIndex, resolvedPost]);

  useEffect(() => {
    if (!existingPromptJson) {
      return;
    }

    let signature = null;
    try {
      signature = JSON.stringify(existingPromptJson);
    } catch (error) {
      console.warn('[DesktopImagePromptEditor] Unable to serialize incoming prompt_json', error);
    }

    if (signature && promptJsonSignatureRef.current === signature) {
      return;
    }

    if (signature) {
      promptJsonSignatureRef.current = signature;
    }

    const derivedBlocks = buildBlocksFromPromptJson(existingPromptJson, resolveLabelForKey);
    if (derivedBlocks.length > 0) {
      setBlocks(derivedBlocks);
    }

    const metadata = existingPromptJson.metadata || {};
    if (typeof metadata.quality === 'string' && metadata.quality) {
      setQuality(metadata.quality);
    }
    if (typeof metadata.tone === 'string' && metadata.tone) {
      setTone(metadata.tone);
    }
    if (metadata.creativity !== undefined) {
      const normalizedCreativity = clampCreativity(metadata.creativity);
      if (normalizedCreativity !== undefined) {
        setCreativity(normalizedCreativity);
      }
    }

    const ratioValue = metadata.aspect_ratio || metadata.ratio;
    const dimensions = metadata.dimensions || metadata.dimension;
    const width = metadata.width || (dimensions && (dimensions.width || dimensions.w));
    const height = metadata.height || (dimensions && (dimensions.height || dimensions.h));

    if (typeof ratioValue === 'string') {
      if (/^\d+\s*:\s*\d+$/.test(ratioValue)) {
        setAspectRatio(ratioValue.replace(/\s+/g, ''));
      } else if (/^\d+\s*x\s*\d+$/i.test(ratioValue)) {
        const [w, h] = ratioValue.toLowerCase().split('x').map((part) => part.trim());
        if (w && h) {
          setAspectRatio('custom');
          setCustomWidth(w);
          setCustomHeight(h);
        }
      }
    } else if (width && height) {
      setAspectRatio('custom');
      setCustomWidth(String(width));
      setCustomHeight(String(height));
    }
  }, [existingPromptJson, resolveLabelForKey]);

  const handleBlockChange = useCallback((idx, value) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? value : b)));
  }, []);

  const buildMetadataHeader = useCallback(() => {
    const dimensions = aspectRatio === 'custom' ? `${customWidth}x${customHeight}` : aspectRatio;
    return t('postReview.imageEditor.defaults.metadata', {
      ratio: dimensions,
      quality,
      tone,
      creativity
    });
  }, [aspectRatio, customWidth, customHeight, creativity, quality, tone, t]);

  const buildLegacyFallbackPrompt = useCallback(() => (
    t('postReview.imageEditor.defaults.legacyPromptFallback', {
      defaultValue: 'Genera una nuova immagine seguendo i blocchi attuali con rapporto e stile selezionati.'
    })
  ), [t]);

  const normalizePromptField = useCallback((value, { fallback, max = PROMPT_MAX_LENGTH } = {}) => {
    const trimmed = (value || '').trim();

    if (trimmed.length >= PROMPT_MIN_LENGTH) {
      return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
    }

    const safeFallback = (fallback || '').trim();
    if (safeFallback) {
      return safeFallback.length > max ? safeFallback.slice(0, max) : safeFallback;
    }

    return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
  }, []);

  const buildPrompt = useCallback(() => {
    const joined = blocks.map((b) => b.trim()).filter(Boolean).join('\n');
    const header = buildMetadataHeader();
    return `${header}\n\n${joined}`.trim();
  }, [blocks, buildMetadataHeader]);

  const normalizeAspectRatio = useCallback(() => {
    if (aspectRatio === 'custom') {
      const width = parseInt(customWidth, 10);
      const height = parseInt(customHeight, 10);
      if (width > 0 && height > 0) {
        const divisor = gcd(width, height);
        const normalizedWidth = Math.round(width / divisor);
        const normalizedHeight = Math.round(height / divisor);
        if (normalizedWidth > 0 && normalizedHeight > 0) {
          return `${normalizedWidth}:${normalizedHeight}`;
        }
      }
      return '4:5';
    }

    const allowed = new Set(['1:1', '4:5', '5:4', '16:9', '9:16', '3:4', '4:3']);
    if (allowed.has(aspectRatio)) {
      return aspectRatio;
    }

    if (aspectRatio === '21:9') {
      return '16:9';
    }

    return '4:5';
  }, [aspectRatio, customWidth, customHeight]);

  const normalizedRatio = useMemo(() => normalizeAspectRatio(), [normalizeAspectRatio]);
  const promptModeType = useMemo(() => (mode === 'simple' ? 'modify' : 'generate'), [mode]);
  const isGenerateMode = promptModeType === 'generate';
  const trimmedSimplePrompt = useMemo(() => simplePromptText.trim(), [simplePromptText]);

  useEffect(() => {
    if (generationPendingRef.current && effectiveCurrentImageUrl) {
      lastGeneratedImageRef.current = effectiveCurrentImageUrl;
      setHasGeneratedImage(true);
      generationPendingRef.current = false;
      return;
    }

    if (isGenerateMode && (lastGeneratedImageRef.current || effectiveCurrentImageUrl)) {
      setHasGeneratedImage(true);
      return;
    }

    setHasGeneratedImage(false);
  }, [effectiveCurrentImageUrl, isGenerateMode]);

  const displayImageUrl = useMemo(() => {
    const baseUrl = regeneratedImageUrl || (
      isGenerateMode
        ? (hasGeneratedImage ? (currentImageUrl || lastGeneratedImageRef.current) : null)
        : currentImageUrl
    );

    return baseUrl ? withCacheBuster(baseUrl, regeneratedImageToken) : null;
  }, [
    currentImageUrl,
    hasGeneratedImage,
    isGenerateMode,
    regeneratedImageToken,
    regeneratedImageUrl
  ]);

  const {
    bind: previewHoldBind,
    displayUrl: previewImageUrl,
    hasPrevious: hasPreviousPreviewImage,
    isHoldingPrevious: isHoldingPreviousPreviewImage
  } = useHoldToRevealPreviousImage(displayImageUrl, { resetKey: editingSessionKey });

  const shouldRenderCurrentImage = isGenerateMode ||
    Boolean(currentImageUrl) ||
    Boolean(regeneratedImageUrl) ||
    Boolean(lastGeneratedImageRef.current);

  const canApplyImageToPost = Boolean(regeneratedImageToken && regeneratedImageUrl && postId);

  const imageGenerationPercent = useEstimatedProgressPercent(loading, 30000, 99);
  const promptBlocksPercent = useEstimatedProgressPercent(isRegeneratingBlocks, 30000, 99);

  useEffect(() => {
    if (isLoading || showImageGallery) {
      return;
    }
    schedulePreviewUpdate();
  }, [
    canApplyImageToPost,
    displayImageUrl,
    isApplyingImageToPost,
    isLoading,
    loading,
    mode,
    schedulePreviewUpdate,
    showImageGallery
  ]);

  const derivedPromptJson = useMemo(() => buildPromptJsonFromBlocks(blocks, {
    normalizedRatio,
    quality,
    tone,
    creativity,
    simplePrompt: trimmedSimplePrompt,
    referenceImages: [],
    mode: promptModeType
  }), [
    blocks,
    normalizedRatio,
    quality,
    tone,
    creativity,
    trimmedSimplePrompt,
    promptModeType
  ]);

  const hasPromptJsonContent = useMemo(() => {
    if (!derivedPromptJson || typeof derivedPromptJson !== 'object') {
      return false;
    }
    return Object.keys(derivedPromptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && derivedPromptJson[key]);
  }, [derivedPromptJson]);

  const buildUserPrompt = useCallback(() => {
    const structuredPrompt = buildPrompt().trim();

    if (mode === 'simple') {
      return simplePromptText.trim();
    }

    return structuredPrompt;
  }, [buildPrompt, mode, simplePromptText]);

  const prepareReferenceImages = useCallback(async () => {
    if (isGenerateMode) {
      return {
        references: [],
        baseReferenceIncluded: false,
        warnings: []
      };
    }

    const seenReferences = new Set();
    let baseReference = null;
    const extraReferences = [];
    const warnings = [];

    const appendExtraReference = (url) => {
      if (url && !seenReferences.has(url) && url !== baseReference) {
        seenReferences.add(url);
        extraReferences.push(url);
      }
    };

    const attemptAddBaseReference = (candidate, source) => {
      if (!candidate) {
        return;
      }

      const { url, reason } = resolveReferenceCandidate(candidate);
      if (url && !baseReference) {
        baseReference = url;
        seenReferences.add(url);
      } else if (reason && reason !== 'empty') {
        warnings.push({ source, reason, value: candidate });
      }
    };

    const attemptAddReference = (candidate, source) => {
      if (source === 'base') {
        attemptAddBaseReference(candidate, source);
        return;
      }

      if (!candidate) {
        return;
      }

      const { url, reason } = resolveReferenceCandidate(candidate);
      if (url) {
        appendExtraReference(url);
      } else if (reason && reason !== 'empty') {
        warnings.push({ source, reason, value: candidate });
      }
    };

    const mediaFile = Array.isArray(resolvedPost?.media_files) ? resolvedPost.media_files[resolvedImageIndex] : null;
    const fallbackMediaFile = Array.isArray(resolvedPost?.media_files) ? resolvedPost.media_files[0] : null;
    const baseCandidates = [
      regeneratedImageUrl,
      currentImageUrl,
      currentMediaItem?.url,
      currentMediaItem?.previewUrl,
      currentMediaItem?.imageUrl,
      currentMediaItem?.src,
      mediaFile?.url,
      mediaFile?.previewUrl,
      mediaFile?.imageUrl,
      mediaFile?.src,
      fallbackMediaFile?.url,
      fallbackMediaFile?.previewUrl,
      fallbackMediaFile?.imageUrl,
      fallbackMediaFile?.src
    ];

    baseCandidates.forEach((candidate) => attemptAddReference(candidate, 'base'));

    if (!baseReference && currentMediaItem?.type === 'file') {
      warnings.push({ source: 'base', reason: 'unuploaded-file' });
    }

    if (promptImage) {
      attemptAddReference(promptImage.url, 'prompt');
      attemptAddReference(promptImage.previewUrl, 'prompt');

      if (promptImage.file && (!promptImage.url || BLOB_URL_REGEX.test(promptImage.url))) {
        warnings.push({ source: 'prompt', reason: 'unuploaded-file' });
      }
    }

    const references = baseReference
      ? [baseReference, ...extraReferences.filter((ref) => ref !== baseReference)]
      : extraReferences;

    return {
      references,
      baseReferenceIncluded: Boolean(baseReference),
      warnings
    };
  }, [currentImageUrl, currentMediaItem, promptImage, isGenerateMode, regeneratedImageUrl, resolvedPost, resolvedImageIndex]);

  const handleApplyImageToPost = useCallback(async () => {
    if (!postId || !regeneratedImageUrl) {
      notify({
        variant: 'error',
        title: t('postReview.imageEditor.alerts.error', { defaultValue: 'Errore' })
      });
      return;
    }

    if (isApplyingImageToPost || loading) {
      return;
    }

    const confirmed = await confirm({
      title: t('postReview.imageEditor.apply.confirmTitle', { defaultValue: 'Aggiornare l’immagine del post?' }),
      variant: 'warning',
      confirmText: t('postReview.imageEditor.apply.confirmButton', { defaultValue: 'Aggiorna' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
    });

    if (!confirmed) {
      return;
    }

    const absoluteNewUrl = toAbsoluteMediaUrl(regeneratedImageUrl) || regeneratedImageUrl;

    const existingMediaUrls = (() => {
      const candidates = [
        Array.isArray(resolvedPost?.media_urls) ? resolvedPost.media_urls : null,
        Array.isArray(resolvedPost?.mediaUrls) ? resolvedPost.mediaUrls : null,
        Array.isArray(resolvedPost?.images) ? resolvedPost.images : null,
        Array.isArray(resolvedPost?.mediaArray)
          ? resolvedPost.mediaArray.map((item) => item?.url || item?.previewUrl).filter(Boolean)
          : null
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) {
          return candidate;
        }
      }
      return [];
    })();

    const sanitizedExisting = existingMediaUrls
      .filter((candidate) => typeof candidate === 'string')
      .map((candidate) => toAbsoluteMediaUrl(candidate) || candidate)
      .filter(Boolean);

    const updatedMediaUrls = (() => {
      const urls = [...sanitizedExisting];
      const safeIndex = typeof resolvedImageIndex === 'number' && resolvedImageIndex >= 0
        ? resolvedImageIndex
        : 0;

      if (safeIndex < urls.length) {
        urls[safeIndex] = absoluteNewUrl;
      } else {
        urls.push(absoluteNewUrl);
      }

      return urls;
    })();

    if (!updatedMediaUrls.length) {
      notify({
        variant: 'error',
        title: t('postReview.imageEditor.alerts.error', { defaultValue: 'Errore' })
      });
      return;
    }

    setIsApplyingImageToPost(true);
    try {
      const mediaUpdates = updatedMediaUrls.map((url) => ({ type: 'url', url }));
      const response = await postService.updatePost(postId, {}, mediaUpdates);

      const updatedPost =
        response?.post ||
        response?.data?.post ||
        response?.result?.post ||
        response?.results?.post ||
        null;

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('postUpdated', {
          detail: { postId, updatedPost }
        }));
      }

      notify({
        variant: 'success',
        title: t('postReview.imageEditor.apply.success', { defaultValue: 'Immagine aggiornata nel post' })
      });

      if (typeof onNavigate === 'function') {
        onNavigate(`/desktop/posts?postId=${encodeURIComponent(String(postId))}`);
      } else if (typeof onBack === 'function') {
        onBack();
      }
    } catch (error) {
      console.error('[DesktopImagePromptEditor] Failed to apply image to post', error);
      notify({
        variant: 'error',
        title: error?.message || t('postReview.imageEditor.alerts.error', { defaultValue: 'Errore' })
      });
    } finally {
      setIsApplyingImageToPost(false);
    }
  }, [
    confirm,
    isApplyingImageToPost,
    loading,
    notify,
    onBack,
    onNavigate,
    postId,
    regeneratedImageUrl,
    resolvedImageIndex,
    resolvedPost,
    t
  ]);

  const handleRegenerateAll = useCallback(async () => {
    if (!postId) {
      notify({
        variant: 'error',
        title: t('postReview.imageEditor.alerts.missingPostId')
      });
      return;
    }

    const userPrompt = buildUserPrompt();
    const trimmedUserPrompt = userPrompt.trim();
    if (!trimmedUserPrompt) {
      notify({
        variant: 'warning',
        title: t('postReview.imageEditor.alerts.missingPrompt')
      });
      return;
    }

    let normalizedUserPrompt = trimmedUserPrompt;
    let normalizedPromptText = null;

    if (isModifyMode) {
      if (normalizedUserPrompt.length > PROMPT_MAX_LENGTH) {
        normalizedUserPrompt = normalizedUserPrompt.slice(0, PROMPT_MAX_LENGTH);
      }
    } else {
      const promptText = buildPrompt();
      const fallbackLegacyPrompt = buildLegacyFallbackPrompt();
      normalizedUserPrompt = normalizePromptField(trimmedUserPrompt, { fallback: fallbackLegacyPrompt });
      normalizedPromptText = normalizePromptField(promptText, {
        fallback: normalizedUserPrompt,
        max: PROMPT_MAX_LENGTH
      });
    }

    setLoading(true);
    generationPendingRef.current = true;

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }

      const { references: referenceImages, baseReferenceIncluded, warnings } = await prepareReferenceImages();

      if (warnings.length) {
        warnings.forEach((warning) => {
          console.warn('[DesktopImagePromptEditor] Skipping reference image for AI request', warning);
        });
      }

      if (isModifyMode && !baseReferenceIncluded) {
        throw new Error(t('postReview.imageEditor.alerts.baseReferenceError', {
          defaultValue: "Impossibile usare l'immagine corrente come riferimento AI. Salva il post o seleziona un'immagine già caricata."
        }));
      }

      const promptWarnings = warnings.filter(
        (warning) => warning.source === 'prompt' && warning.reason === 'unuploaded-file'
      );

      if (promptWarnings.length) {
        notify({
          variant: 'info',
          title: t('postReview.imageEditor.alerts.localReferencesIgnored', {
            defaultValue: 'Le immagini di riferimento caricate localmente non possono essere inviate finché non vengono salvate sul server. Verranno ignorate per questa rigenerazione.'
          })
        });
      }

      const payload = (() => {
        if (isModifyMode) {
          const basePayload = {
            prompt_text: normalizedUserPrompt
          };

          if (referenceImages.length) {
            basePayload.reference_images = referenceImages;
          }

          return basePayload;
        }

        const generatePayload = {
          user_prompt: normalizedUserPrompt,
          count: 1,
          ratio: normalizedRatio
        };

        if (normalizedPromptText) {
          generatePayload.prompt_text = normalizedPromptText;
        }

        if (referenceImages.length) {
          generatePayload.reference_images = referenceImages;
        }

        if (hasPromptJsonContent) {
          const promptJson = buildPromptJsonFromBlocks(blocks, {
            normalizedRatio,
            quality,
            tone,
            creativity,
            simplePrompt: trimmedSimplePrompt || undefined,
            referenceImages,
            mode: promptModeType,
            baseReferenceIncluded,
            includeReferenceStats: true
          });

          const promptJsonSchema = buildPromptJsonSchema(promptJson);

          const hasJsonPayload = Object.keys(promptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && promptJson[key]);

          if (hasJsonPayload) {
            generatePayload.prompt_json = promptJson;
          }

          if (promptJsonSchema && hasJsonPayload) {
            generatePayload.prompt_json_schema = promptJsonSchema;
          }
        }

        return generatePayload;
      })();

      const response = await apiClient.post(
        `/api/v1/posts/${postId}/regenerate/image`,
        payload
      );

      const existingUrls = [
        regeneratedImageUrl,
        currentImageUrl,
        currentMediaItem?.url,
        currentMediaItem?.previewUrl
      ].filter((candidate) => typeof candidate === 'string');

      const deltaImages = extractRegeneratedImageUrls(response, { existingUrls, excludeExisting: true });
      const allImages = extractRegeneratedImageUrls(response);
      const candidateImages = deltaImages.length ? deltaImages : allImages;
      const finalImageUrl = candidateImages[0] || allImages[allImages.length - 1] || null;

      if (finalImageUrl) {
        setRegeneratedImageUrl(finalImageUrl);
        setRegeneratedImageToken(Date.now());
        lastGeneratedImageRef.current = finalImageUrl;
        setHasGeneratedImage(true);
      } else {
        const fallbackUrl = currentImageUrl ? toAbsoluteMediaUrl(currentImageUrl) || currentImageUrl : null;
        if (fallbackUrl) {
          setRegeneratedImageUrl(fallbackUrl);
          setRegeneratedImageToken(Date.now());
        } else {
          console.warn('[DesktopImagePromptEditor] Regeneration response did not include an image URL', response);
        }
      }

      generationPendingRef.current = false;

      if (typeof onRegenerate === 'function') {
        await onRegenerate({
          type: 'image',
          prompt: normalizedUserPrompt,
          imageIndex: resolvedImageIndex,
          payload,
          response
        });
      }

      notify({
        variant: 'success',
        title: t('postReview.imageEditor.alerts.success')
      });
    } catch (error) {
      console.error('[DesktopImagePromptEditor] Regenerate failed', error);
      const message = error?.message || t('postReview.imageEditor.alerts.error');
      notify({
        variant: 'error',
        title: message
      });
      generationPendingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [
    apiClient,
    blocks,
    buildPrompt,
    buildLegacyFallbackPrompt,
    normalizePromptField,
    buildUserPrompt,
    creativity,
    hasPromptJsonContent,
    resolvedImageIndex,
    normalizedRatio,
    onRegenerate,
    postId,
    promptModeType,
    prepareReferenceImages,
    quality,
    tone,
    trimmedSimplePrompt,
    t,
    isModifyMode,
    currentImageUrl,
    currentMediaItem,
    regeneratedImageUrl,
    notify
  ]);

  const handleGenerateImage = useCallback(async () => {
    if (loading) return;

    const confirmed = await confirm({
      title: t('postReview.imageEditor.alerts.confirm'),
      variant: 'warning',
      confirmText: t('postReview.imageEditor.actions.regenerate', { defaultValue: 'Rigenera' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
    });

    if (confirmed) {
      handleRegenerateAll();
    }
  }, [confirm, handleRegenerateAll, loading, t]);

  const handleClosePromptBlocksModal = useCallback(() => {
    if (isRegeneratingBlocks) {
      return;
    }
    setShowPromptBlocksModal(false);
  }, [isRegeneratingBlocks]);

  const handlePromptBlocksRegeneration = useCallback(async ({ prompt: modalPrompt }) => {
    const trimmedInstructions = (modalPrompt || '').trim();

    if (!postId) {
      notify({
        variant: 'error',
        title: t('postReview.imageEditor.alerts.missingPostId')
      });
      return;
    }

    if (!trimmedInstructions) {
      notify({
        variant: 'warning',
        title: t('postReview.imageEditor.alerts.missingPrompt')
      });
      return;
    }

    setIsRegeneratingBlocks(true);

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }

      const metadataHeader = buildMetadataHeader();
      const enrichedInstructions = [
        trimmedInstructions,
        metadataHeader
      ].filter(Boolean).join('\n\n');

      const payload = {
        prompt_text: trimmedInstructions
      };

      if (enrichedInstructions) {
        payload.user_prompt = enrichedInstructions;
      }

      if (hasPromptJsonContent) {
        const promptJsonSchema = buildPromptJsonSchema(derivedPromptJson);
        if (promptJsonSchema) {
          payload.prompt_json_schema = promptJsonSchema;
        }
      }

      const response = await apiClient.post(
        '/api/v1/posts/prompt-json',
        payload
      );

      const resolvedPromptJson = response?.prompt_json || null;

      const resolvedPromptText =
        response?.prompt_text ||
        '';

      if (resolvedPromptJson) {
        try {
          promptJsonSignatureRef.current = JSON.stringify(resolvedPromptJson);
        } catch (error) {
          console.warn('[DesktopImagePromptEditor] Failed to serialize regenerated prompt_json', error);
        }

        const regeneratedBlocks = buildBlocksFromPromptJson(resolvedPromptJson, resolveLabelForKey);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      } else if (resolvedPromptText) {
        const regeneratedBlocks = splitPromptIntoBlocks(resolvedPromptText);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      }

      setShowPromptBlocksModal(false);
      notify({
        variant: 'success',
        title: t('postReview.imageEditor.alerts.blocksSuccess', { defaultValue: 'Blocchi rigenerati! Ricordati di salvare le modifiche.' })
      });
    } catch (error) {
      console.error('[DesktopImagePromptEditor] Prompt blocks regeneration failed', error);
      const message = error?.message || t('postReview.imageEditor.alerts.blocksError', { defaultValue: 'Impossibile rigenerare i blocchi del prompt. Riprova.' });
      notify({
        variant: 'error',
        title: message
      });
    } finally {
      setIsRegeneratingBlocks(false);
    }
  }, [
    apiClient,
    buildMetadataHeader,
    derivedPromptJson,
    hasPromptJsonContent,
    postId,
    resolveLabelForKey,
    t,
    notify
  ]);

  const triggerPromptImageUpload = useCallback(() => {
    setShowImageGallery(true);
  }, []);

  const setPromptImageFromFile = useCallback((file) => {
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[DesktopImagePromptEditor] Failed to revoke previous object URL', error);
      }
    }

    promptImageObjectUrlRef.current = previewUrl;

    setPromptImage({
      id: previewUrl,
      url: previewUrl,
      name: file.name || fallbackReferenceName,
      file
    });
  }, [fallbackReferenceName]);

  const handleReferenceFiles = useCallback((fileList) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const file = fileList[0];
    setReferenceError('');

    const fileType = (file.type || '').toLowerCase();
    const fileExt = (file.name || '').split('.').pop()?.toLowerCase();
    const isAllowedType = allowedReferenceTypes.includes(fileType) || ['png', 'jpg', 'jpeg'].includes(fileExt || '');

    if (!isAllowedType) {
      setReferenceError(t('postReview.imageEditor.references.typeError', { defaultValue: 'Carica solo file PNG o JPG.' }));
      return;
    }

    if (file.size > referenceMaxSizeBytes) {
      setReferenceError(t('postReview.imageEditor.references.sizeError', { maxMB: referenceMaxSizeMB, defaultValue: 'Limite massimo 5MB.' }));
      return;
    }

    setPromptImageFromFile(file);
  }, [allowedReferenceTypes, referenceMaxSizeBytes, setPromptImageFromFile, t]);

  const handleReferenceDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingReference(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      handleReferenceFiles(files);
    }
  }, [handleReferenceFiles]);

  const handleReferenceDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingReference(true);
  }, []);

  const handleReferenceDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDraggingReference(false);
  }, []);

  const handleReferenceDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleReferenceBrowse = useCallback(() => {
    setReferenceError('');
    if (referenceInputRef.current) {
      referenceInputRef.current.click();
    }
  }, []);

  const handleImageSelect = useCallback((image) => {
    if (image?.files && image.files.length > 0) {
      const file = image.files[0];
      handleReferenceFiles([file]);
    } else if (image) {
      const selectedUrl = image.selectedUrl || image.displayUrl || image.url;
      if (selectedUrl) {
        setPromptImage({
          id: image.id,
          url: selectedUrl,
          name: image.name || image.title || fallbackReferenceName,
          previewUrl: image.previewUrl
        });
        setReferenceError('');
      }
    }
    setShowImageGallery(false);
  }, [fallbackReferenceName, handleReferenceFiles]);

  const handleDeletePromptImage = useCallback(() => {
    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[DesktopImagePromptEditor] Failed to revoke object URL on delete', error);
      }
      promptImageObjectUrlRef.current = null;
    }
    setPromptImage(null);
    setReferenceError('');
  }, []);

  const handleBackFromGallery = useCallback(() => {
    setShowImageGallery(false);
  }, []);

  const handleBackClick = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (typeof onNavigate === 'function') {
      onNavigate('/desktop/posts');
    }
  }, [onBack, onNavigate]);

  useEffect(() => () => {
    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[DesktopImagePromptEditor] Failed to revoke object URL on unmount', error);
      }
      promptImageObjectUrlRef.current = null;
    }
  }, []);

  const headerSummary = useMemo(() => {
    const title = resolvedPost?.title || t('postReview.imageEditor.header.title', { defaultValue: 'Editor immagini AI' });
    const brand = resolvedPost?.company_name || resolvedPost?.brand || '';
    if (brand) {
      return `${title} · ${brand}`;
    }
    return title;
  }, [resolvedPost, t]);

  const referenceSection = (
    <div className="loopino-reference-section">
      <div className="loopino-section-heading">
        <div>
          <p className="loopino-section-eyebrow">{t('postReview.imageEditor.references.title')}</p>
          <h3>{t('postReview.imageEditor.references.description', { defaultValue: "Aggiungi immagini guida per l'AI" })}</h3>
        </div>
      </div>

      <div
        className={`loopino-reference-dropzone ${isDraggingReference ? 'dragging' : ''}`}
        onDragEnter={handleReferenceDragEnter}
        onDragOver={handleReferenceDragOver}
        onDragLeave={handleReferenceDragLeave}
        onDrop={handleReferenceDrop}
      >
        <input
          ref={referenceInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          hidden
          onChange={(event) => {
            handleReferenceFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <div className="loopino-reference-drop-content">
          <div className="loopino-reference-drop-icon">
            <img src={iconUpload} alt="" aria-hidden="true" className="loopino-icon-invert" />
          </div>
          <p className="loopino-reference-drop-title">
            {t('postReview.imageEditor.references.dropTitle', { defaultValue: 'Trascina qui le immagini' })}
          </p>
          <p className="loopino-reference-drop-sub">
            PNG o JPG · {t('postReview.imageEditor.references.maxSize', { size: referenceMaxSizeMB, defaultValue: 'Max 5MB' })}
          </p>
          <div className="loopino-reference-drop-actions">
            <button
              type="button"
              className="loopino-button ghost"
              onClick={handleReferenceBrowse}
            >
              <img src={iconUpload} alt="" aria-hidden="true" className="loopino-button-icon loopino-icon-invert" />
              {t('postReview.imageEditor.references.upload')}
            </button>
            <button
              type="button"
              className="loopino-button ghost"
              onClick={triggerPromptImageUpload}
            >
              <img src={iconImage} alt="" aria-hidden="true" className="loopino-button-icon" />
              {t('postReview.imageEditor.references.gallery')}
            </button>
          </div>
        </div>
      </div>

      {referenceError && (
        <div className="loopino-reference-error" role="alert">
          {referenceError}
        </div>
      )}

      {promptImage ? (
        <div className="loopino-reference-file">
          <div className="loopino-reference-file-main">
            <div className="loopino-reference-thumb">
              <img
                src={promptImage.url}
                alt={promptImage.name}
                onError={(e) => {
                  e.target.src = placeholderImage;
                }}
              />
            </div>
            <div className="loopino-reference-info">
              <p className="loopino-reference-title">{promptImage.name}</p>
              <p className="loopino-reference-sub">
                {promptImage.file?.size ? formatBytes(promptImage.file.size) : t('postReview.imageEditor.references.title')}
              </p>
            </div>
          </div>
          <button
            className="loopino-icon-button"
            onClick={handleDeletePromptImage}
            aria-label={t('postReview.imageEditor.references.deleteAria')}
            type="button"
          >
            <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt')} />
          </button>
        </div>
      ) : (
        <p className="loopino-reference-helper">
          {t('postReview.imageEditor.references.helper', { defaultValue: 'Trascina una singola immagine di riferimento o scegli dal dispositivo.' })}
        </p>
      )}
    </div>
  );

  if (showImageGallery) {
    return (
      <>
        <DesktopImageGalleryPage
          onClose={handleBackFromGallery}
          onSelect={handleImageSelect}
          viewerOrigin="desktop-image-editor"
        />
        <AIRegenerationModal
          isOpen={showPromptBlocksModal}
          onClose={handleClosePromptBlocksModal}
          type="blocks"
          post={resolvedPost}
          imageIndex={resolvedImageIndex}
          onRegenerate={handlePromptBlocksRegeneration}
          isRegenerating={isRegeneratingBlocks}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <div className="loopino-desktop-shell loopino-desktop-image-editor-shell">
          <header className="loopino-desktop-image-editor-header" ref={pageHeaderRef}>
            <div>
              <Skeleton variant="rounded" width={140} height={16} shimmer />
              <Skeleton variant="rounded" width={320} height={32} shimmer style={{ marginTop: 8 }} />
              <Skeleton variant="rounded" width={420} height={18} shimmer style={{ marginTop: 8 }} />
            </div>
            <div className="loopino-desktop-image-actions">
              <Skeleton variant="rounded" width={120} height={44} shimmer />
            </div>
          </header>

          <div className="loopino-image-editor-grid">
            <div className="loopino-image-editor-col">
              <Skeleton variant="rounded" width="100%" height={380} shimmer />
              <Skeleton variant="rounded" width="100%" height={160} shimmer style={{ marginTop: 16 }} />
            </div>
            <div className="loopino-image-editor-col">
              <Skeleton variant="rounded" width="100%" height={540} shimmer />
              <Skeleton variant="rounded" width="100%" height={220} shimmer style={{ marginTop: 16 }} />
            </div>
          </div>
        </div>

        <AIRegenerationModal
          isOpen={showPromptBlocksModal}
          onClose={handleClosePromptBlocksModal}
          type="blocks"
          post={resolvedPost}
          imageIndex={resolvedImageIndex}
          onRegenerate={handlePromptBlocksRegeneration}
          isRegenerating={isRegeneratingBlocks}
        />
      </>
    );
  }

  const previewClassName = [
    'loopino-desktop-image-preview-sticky',
    previewPinMode === 'fixed' ? 'is-fixed' : '',
    previewPinMode === 'bottom' ? 'is-bottom' : ''
  ].filter(Boolean).join(' ');
  const mergedPreviewStyle = {
    ...(previewPanelStyle || {}),
    ...(previewDynamicStyle || {})
  };

  return (
    <>
      <div className="loopino-desktop-shell loopino-desktop-image-editor-shell">
        <header className="loopino-desktop-image-editor-header" ref={pageHeaderRef}>
          <div>
            <p className="loopino-desktop-image-eyebrow">
              {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
            </p>
            <h1>{t('postReview.imageEditor.header.title')}</h1>
            <p className="loopino-desktop-image-summary">{headerSummary}</p>
          </div>
          <div className="loopino-desktop-image-actions">
            {(onBack || onNavigate) && (
              <button
                type="button"
                className="loopino-button ghost"
                onClick={(e) => {
                  e.preventDefault();
                  handleBackClick();
                }}
              >
                <BackIcon />
                {t('common.actions.back', { defaultValue: 'Indietro' })}
              </button>
            )}
          </div>
        </header>

        <div className="loopino-image-editor-grid" ref={imageEditorGridRef}>
          <div
            className="loopino-desktop-image-preview-placeholder"
            ref={imagePreviewPlaceholderRef}
            aria-hidden
            style={previewPlaceholderStyle}
          />
          <div
            className={previewClassName}
            style={mergedPreviewStyle}
            ref={imagePreviewPanelRef}
          >
            <div className="loopino-card loopino-image-preview-card">
              <div className="loopino-card-header">
                <div>
                  <p className="loopino-card-eyebrow">{t('postReview.imageEditor.currentImage', { defaultValue: 'Immagine attuale' })}</p>
                  <h3>{currentImageName}</h3>
                </div>
              </div>

              <div className="loopino-image-preview-frame" {...previewHoldBind}>
                {shouldRenderCurrentImage ? (
                  <>
                    {previewImageUrl ? (
                      <img
                        src={previewImageUrl}
                        alt={isHoldingPreviousPreviewImage ? `${currentImageName} (precedente)` : currentImageName}
                        title={hasPreviousPreviewImage ? t('postReview.imageEditor.preview.holdForPrevious', { defaultValue: "Tieni premuto per vedere l'immagine precedente" }) : undefined}
                        draggable={false}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                    ) : (
                      <div className="loopino-image-placeholder">
                        <img src={iconImage} alt={t('postReview.imageEditor.references.emptyAlt')} />
                        <p>{t('postReview.imageEditor.currentImagePlaceholder', { defaultValue: "Genera l'immagine" })}</p>
                      </div>
                    )}
                    {loading && (
                      <AILoadingOverlay text={t('postReview.imageEditor.overlay.generating')} />
                    )}
                  </>
                ) : (
                  <div className="loopino-image-placeholder">
                    <img src={iconImage} alt={t('postReview.imageEditor.references.emptyAlt')} />
                    <p>{t('postReview.imageEditor.currentImagePlaceholder', { defaultValue: "Genera l'immagine" })}</p>
                  </div>
                )}
              </div>

              <div className="generation-settings-toggle loopino-image-base-toggle">
                <div className="generation-toggle-row generation-toggle-row-stacked">
                  <div className="generation-toggle-text">
                    <h3 className="generation-toggle-title">
                      {t('postReview.imageEditor.baseToggle.title', { defaultValue: 'Immagine base' })}
                    </h3>
                    <p className="generation-toggle-subtitle">
                      {t('postReview.imageEditor.baseToggle.helper', { defaultValue: "Usa l'immagine attuale come base per le variazioni." })}
                    </p>
                  </div>
                  <DualOptionToggle
                    active={isModifyMode}
                    onChange={(next) => setMode(next ? 'simple' : 'pro')}
                    leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                    rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                    disabled={!effectiveCurrentImageUrl || loading}
                    ariaLabel={t('postReview.imageEditor.baseToggle.aria', { defaultValue: 'Attiva immagine base' })}
                    className="ai-image-base-toggle"
                  />
                </div>
              </div>

              {canApplyImageToPost && (
                <div className="loopino-image-preview-actions">
                  <button
                    type="button"
                    className="loopino-button"
                    onClick={handleApplyImageToPost}
                    disabled={loading || isApplyingImageToPost}
                  >
                    {isApplyingImageToPost
                      ? t('postReview.imageEditor.apply.updating', { defaultValue: 'Aggiornamento...' })
                      : t('postReview.imageEditor.apply.applyButton', { defaultValue: 'Aggiorna immagine del post' })}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="loopino-image-editor-col">
            <div className="loopino-glass-panel loopino-image-editor-panel">
              <div className="loopino-mode-toggle">
                <button
                  className={`loopino-mode-option ${mode === 'simple' ? 'active' : ''}`}
                  onClick={() => setMode('simple')}
                >
                  {t('postReview.imageEditor.mode.simple')}
                </button>
                <button
                  className={`loopino-mode-option ${mode === 'pro' ? 'active' : ''}`}
                  onClick={() => setMode('pro')}
                >
                  {t('postReview.imageEditor.mode.pro')}
                </button>
              </div>

              {mode === 'simple' && (
                <div className="loopino-image-editor-section">
                  <div className="loopino-section-heading">
                    <div>
                      <h3>{t('postReview.imageEditor.simple.title')}</h3>
                    </div>
                  </div>
                  <textarea
                    className="loopino-textarea"
                    value={simplePromptText}
                    onChange={(e) => setSimplePromptText(e.target.value)}
                    placeholder={t('postReview.imageEditor.simple.placeholder', { defaultValue: 'Es: sostituisci il soggetto con un drago di carta...' })}
                    rows={6}
                  />
                  {referenceSection}
                </div>
              )}

              {mode === 'pro' && (
                <>
                  <div className="loopino-image-editor-section">
                    <div className="loopino-section-heading">
                      <div>
                        <p className="loopino-section-eyebrow">
                          {t('desktop.prompt.structuredPrompt', { defaultValue: 'Prompt strutturato' })}
                        </p>
                        <h3>{t('postReview.imageEditor.pro.title', { defaultValue: 'Blocchi' })}</h3>
                      </div>
                    </div>

                    <div className="loopino-blocks-grid">
                      {blocks.map((block, idx) => {
                        const rawBlock = typeof block === 'string' ? block : '';
                        const match = PROMPT_BLOCK_REGEX.exec(rawBlock);
                        const label = match
                          ? match[1].trim()
                          : t('postReview.imageEditor.blocks.defaultLabel', { defaultValue: 'Blocco' });
                        const content = match ? match[3] : rawBlock;
                        const placeholder = buildBlockPlaceholder(label, idx);

                        return (
                          <div className="loopino-block-card" key={`block-${idx}`}>
                            <div className="loopino-block-header">
                              <div className="loopino-block-index">{idx + 1}</div>
                              <div className="loopino-block-title">{label}</div>
                            </div>
                            <AutoGrowTextarea
                              className="loopino-textarea loopino-block-textarea"
                              value={content}
                              onChange={(e) => handleBlockChange(idx, `${label}: ${e.target.value}`)}
                              placeholder={placeholder}
                              rows={1}
                              wrap="soft"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="loopino-image-editor-section">
                    <div className="loopino-section-heading">
                      <div>
                        <p className="loopino-section-eyebrow">{t('postReview.imageEditor.options.ratiosTitle')}</p>
                        <h3>{t('postReview.imageEditor.options.ratiosSubtitle', { defaultValue: 'Dimensioni immagine' })}</h3>
                      </div>
                    </div>
                    <div className="loopino-segmented-control">
                      {[{ key: '1:1', label: '1:1' }, { key: '16:9', label: '16:9' }, { key: '9:16', label: '9:16' }, { key: '4:3', label: '4:3' }, { key: '3:4', label: '3:4' }, { key: '4:5', label: '4:5' }, { key: 'custom', label: t('postReview.imageEditor.options.customLabel') }].map(({ key, label }) => (
                        <button
                          key={key}
                          className={`loopino-segment ${aspectRatio === key ? 'active' : ''}`}
                          onClick={() => setAspectRatio(key)}
                          type="button"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {aspectRatio === 'custom' && (
                      <div className="loopino-custom-dimensions">
                        <input
                          type="number"
                          className="loopino-dimension-input"
                          placeholder={t('postReview.imageEditor.options.widthPlaceholder')}
                          value={customWidth}
                          onChange={(e) => setCustomWidth(e.target.value)}
                          min="256"
                          max="2048"
                        />
                        <span className="loopino-dimension-separator">×</span>
                        <input
                          type="number"
                          className="loopino-dimension-input"
                          placeholder={t('postReview.imageEditor.options.heightPlaceholder')}
                          value={customHeight}
                          onChange={(e) => setCustomHeight(e.target.value)}
                          min="256"
                          max="2048"
                        />
                      </div>
                    )}
                  </div>

                  {referenceSection}
                </>
              )}

              <div className="loopino-image-editor-footer">
                {mode === 'pro' && (
                  isRegeneratingBlocks ? (
                    <EstimatedProgressBar
                      className="loopino-estimated-progress-primary"
                      label={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione...' })}
                      percent={promptBlocksPercent}
                      ariaLabel={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione blocchi prompt' })}
                    />
                  ) : (
                    <button
                      type="button"
                      className="loopino-button ghost"
                      onClick={() => setShowPromptBlocksModal(true)}
                      disabled={loading}
                    >
                      <AIIcon />
                      {t('postReview.imageEditor.footer.regenerateBlocks', { defaultValue: 'Rigenera blocchi' })}
                    </button>
                  )
                )}
                {loading ? (
                  <EstimatedProgressBar
                    className="loopino-estimated-progress-primary loopino-estimated-progress-full"
                    label={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione...' })}
                    percent={imageGenerationPercent}
                    ariaLabel={t('postReview.imageEditor.footer.generating', { defaultValue: 'Generazione immagine' })}
                  />
                ) : (
                  <button
                    className="loopino-button primary"
                    onClick={handleGenerateImage}
                    disabled={loading}
                    type="button"
                  >
                    <img src={iconImage} alt={t('postReview.imageEditor.footer.iconAlt')} className="loopino-button-icon" />
                    {t('postReview.imageEditor.footer.generate')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AIRegenerationModal
        isOpen={showPromptBlocksModal}
        onClose={handleClosePromptBlocksModal}
        type="blocks"
        post={resolvedPost}
        imageIndex={resolvedImageIndex}
        onRegenerate={handlePromptBlocksRegeneration}
        isRegenerating={isRegeneratingBlocks}
      />
    </>
  );
};

const DesktopImagePromptEditorPage = (props) => (
  <DesktopAlertProvider>
    <DesktopImagePromptEditorPageInner {...props} />
  </DesktopAlertProvider>
);

export default DesktopImagePromptEditorPage;
